// @flow

import React from 'react';
import _ from 'lodash';

import Menu, {
  MenuSection,
  MenuSectionsStateful,
} from 'app/common/components/Menu';

import type { FolderBasicsType } from 'app/dashboard/types';

const isShared = folder => folder.isShared;
const toChoice = (folder, { currentFolderIds }) => {
  const isWithinFolder = _.includes(currentFolderIds, folder.id);
  return {
    ...folder,
    iconAfter: isWithinFolder ? 'check-alt' : null,
    // Disable choosing the folder if the zap already lives in it
    isDisabled: isWithinFolder,
    label: folder.title,
    value: folder.id,
  };
};

export type FolderChooserProps = {
  canShowSharedFolders: boolean,
  canToggleSections: boolean,
  currentFolderIds: Array<number>,
  defaultOpen: 'private' | 'shared',
  folders: [
    {
      id: number,
      isShared: boolean,
      title: string,
    }
  ],
  isSectionOpen: (id: number, openIds: Array<number>) => boolean,
  onChoose: (id: number, didConfirm: boolean, folder: FolderBasicsType) => void,
};

const FolderChooser = (props: FolderChooserProps) => {
  const { currentFolderIds } = props;
  const privateFolderChoices = props.folders
    .filter(_.negate(isShared))
    .map(folder => toChoice(folder, { currentFolderIds }));

  const sharedFolderChoices = props.folders
    .filter(isShared)
    .map(folder => toChoice(folder, { currentFolderIds }));

  const privateSection = (
    <MenuSection
      headerKind="dominant"
      icon={props.canShowSharedFolders ? 'lock' : 'folder'}
      id="private"
      items={privateFolderChoices}
      shouldRenderCount={true}
      title={props.canShowSharedFolders ? 'Private Folders' : 'Folders'}
    />
  );

  const sharedSection = !props.canShowSharedFolders ? null : (
    <MenuSection
      headerKind="dominant"
      icon="team"
      id="shared"
      items={sharedFolderChoices}
      shouldRenderCount={true}
      title="Shared Folders"
    />
  );

  return (
    <Menu {...props} kind="square">
      <MenuSectionsStateful
        canToggleSections={props.canToggleSections}
        defaultOpenId={props.defaultOpen}
      >
        {privateSection}
        {sharedSection}
      </MenuSectionsStateful>
    </Menu>
  );
};

FolderChooser.defaultProps = {
  canShowSharedFolders: false,
  canToggleSections: true,
  defaultOpen: 'private',
};

export default FolderChooser;
